import { Link } from 'react-router-dom';
import { Users, Menu, X, Book, Zap, Store, Globe, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isAdminPubkey } from '@/lib/adminConfig';
import { useState } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export function Header() {
  const { user } = useCurrentUser();
  const isAdmin = isAdminPubkey(user?.pubkey);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', label: 'Inicio', icon: Book },
    { to: '/learning-paths', label: 'Rutas de Aprendizaje', icon: Award },
    { to: '/topics', label: 'Temas Fundamentales', icon: Book },
    { to: '/featured-lessons', label: 'Lecciones Destacadas', icon: Zap },
    { to: '/#nostr', label: 'Qué es Nostr', icon: Globe },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-950/80">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-md flex items-center justify-center bg-white p-1">
              <img src="/logo.jpg" alt="Bitcoin Life Panamá Logo" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block">
              <h2 className="text-lg font-bold bg-gradient-to-r from-orange-600 to-amber-600 dark:from-orange-400 dark:to-amber-400 bg-clip-text text-transparent">
                Bitcoin Life Panamá
              </h2>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.to} to={link.to}>
                  <Button variant="ghost" className="gap-2">
                    <Icon className="w-4 h-4" />
                    {link.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/dashboard" className="hidden md:block">
                <Button variant="outline" className="gap-2">
                  <Users className="w-4 h-4" />
                  Dashboard
                </Button>
              </Link>
            )}

            <div className="hidden md:block">
              <LoginArea className="max-w-60" />
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="outline" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col gap-6 mt-8">
                  {/* Logo in mobile menu */}
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <div className="w-10 h-10 rounded-xl overflow-hidden shadow-md flex items-center justify-center bg-white p-1">
                      <img src="/logo.jpg" alt="Bitcoin Life Panamá" className="w-full h-full object-contain" />
                    </div>
                    <div>
                      <h2 className="font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                        Bitcoin Life Panamá
                      </h2>
                    </div>
                  </div>

                  {/* Mobile navigation links */}
                  <nav className="flex flex-col gap-2">
                    {navLinks.map((link) => {
                      const Icon = link.icon;
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Button variant="ghost" className="w-full justify-start gap-3 h-12">
                            <Icon className="w-5 h-5" />
                            {link.label}
                          </Button>
                        </Link>
                      );
                    })}

                    {isAdmin && (
                      <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start gap-3 h-12">
                          <Users className="w-5 h-5" />
                          Dashboard
                        </Button>
                      </Link>
                    )}
                  </nav>

                  {/* Login area in mobile */}
                  <div className="pt-4 border-t">
                    <LoginArea className="w-full flex" />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
